import type { AvailablePaymentStripeOptionsResponse } from '@/types/checkout';
import { loadStripe } from '@stripe/stripe-js';
import { type ComputedRef } from 'vue';

export function usePaymentRequest(eventName: ComputedRef<string>, totalAmount: ComputedRef<number>, isEnablePaymentRequest: ComputedRef<boolean>) {
	const checkoutStore = useCheckoutStore();
	const stripeInternalInstance = ref();
	const stripePaymentInstance = ref();
	const stripeElements = ref();
	const paymentRequestInstance = ref();
	const isGooglePayAvailable = ref(false);
	const isApplePayAvailable = ref(false);
	const isVerifyingAvailablePaymentOption = ref(false);

	async function initStripeInstance() {
		// Init payment instance. Use payment instance to process payment with credit card.
		if (!checkoutStore.stripePublishableKey) {
			throw new TypeError('Stripe public key is not defined');
		}

		if (!checkoutStore.stripeAccountId) {
			throw new TypeError('Stripe account id is not defined');
		}

		isVerifyingAvailablePaymentOption.value = true;
		stripeInternalInstance.value = await loadStripe(checkoutStore.stripePublishableKey);
		stripePaymentInstance.value = await loadStripe(checkoutStore.stripePublishableKey, { stripeAccount: checkoutStore.stripeAccountId});
		if (!stripeInternalInstance.value) {
			isVerifyingAvailablePaymentOption.value = false;
			return;
		}

		stripeElements.value = stripeInternalInstance.value.elements();
		paymentRequestInstance.value = stripeInternalInstance.value.paymentRequest({
			country: 'US',
			currency: 'usd',
			total: {
				label: eventName.value,
				amount: toCents(totalAmount.value),
			},
			requestPayerName: true,
			requestPayerEmail: true,
		});

		const result: AvailablePaymentStripeOptionsResponse = await paymentRequestInstance.value.canMakePayment();
		if (!result) {
			isVerifyingAvailablePaymentOption.value = false;
			return;
		}
		isApplePayAvailable.value = result.applePay;
		isGooglePayAvailable.value = result.googlePay;
		isVerifyingAvailablePaymentOption.value = false;
	}

	function resetState() {
		stripeInternalInstance.value = null;
		stripeElements.value = null;
		paymentRequestInstance.value = null;
		isGooglePayAvailable.value = false;
		isApplePayAvailable.value = false;
		isVerifyingAvailablePaymentOption.value = false;
	}

	watch([isEnablePaymentRequest, totalAmount], ([newIsEnablePaymentRequest, newTotalAmount]) => {
		if (newIsEnablePaymentRequest && newTotalAmount > 0) {
			initStripeInstance();
		} else if (!newIsEnablePaymentRequest && stripeInternalInstance.value) {
			resetState();
		}
	});

	return {
		isGooglePayAvailable,
		isApplePayAvailable,
		isVerifyingAvailablePaymentOption,
		paymentRequestInstance,
		stripePaymentInstance,
	};
}